/**
 * OWNED — Color Echo (SWA-40), 2026-07-15. Baseline: Shopify/horizon v4.1.1
 * @ 68760d9 (no vendor counterpart; this file is entirely ours).
 *
 * Color Echo v1.8.0 — canonical registry: ColorEcho-Install.md §2 — do not
 * edit without bumping the version everywhere (grep "Color Echo v").
 *
 * Defines <color-echo-controller>, rendered by snippets/color-echo-styles.liquid
 * as a SIBLING of <variant-picker> (blocks/variant-picker.liquid) so the picker's
 * morph re-render never destroys it. Liquid pre-computes the per-value token map
 * (accent / tint / on-accent / hover) into the embedded JSON script tag; this
 * module only swaps CSS custom properties on <html> and toggles classes — no
 * color math, no timers, and (Zakeke coexistence) no capture-phase click
 * listeners and nothing near the buy buttons' click path.
 *
 * Morph survival: all listeners are delegated to the section wrapper (never to
 * the picker's inputs), and no picker DOM node is held across events — the
 * checked color radio is re-queried on every change.
 */

// Phase 3 (R1): JS writes only the RAW token values; the consumed --ce-*
// tokens are derived from these in CSS (color-echo-styles.liquid), so the
// reduced-intensity hover preview can re-derive them diluted with no
// client-side color math.
const CE_PROPS = [
  '--ce-raw-accent',
  '--ce-raw-tint',
  '--ce-raw-on-accent',
  '--ce-raw-hover',
  '--ce-raw-accent-border',
  '--ce-raw-accent-ink',
  '--ce-raw-button-accent',
  '--ce-raw-ring-width',
];
// Module version stamp (semver; see the install doc's version registry).
// Bump together with every other "Color Echo v" stamp in the same commit.
const CE_VERSION = '1.8.0';

// Block settings mirrored verbatim from the controller onto <html> so the
// static stylesheet can key off them (html[data-ce-*].ce-active). Removed
// again in disconnectedCallback when the element truly leaves the page.
// Default-true settings (ceBackground, ceDetails) rely on the #mirrorConfig
// blank-skip: a stale block that predates the setting emits an empty attr
// (never mirrored), and the CSS gates only on the explicit 'false' value —
// so a missing attribute behaves as true. v1.8.0 adds ceDetails (dividers /
// counters / rail chevrons), ceEmbeds (Judge.me tab), ceSizes (size-button
// echo mode), and ceButtonModel (CSS skips button overrides on 'stock').
const CE_MIRRORED_DATA = [
  'ceAnnounce',
  'ceColorName',
  'cePrice',
  'ceHeadings',
  'ceIconStyle',
  'ceGallery',
  'ceBackground',
  'ceDetails',
  'ceEmbeds',
  'ceSizes',
  'ceButtonModel',
];
const HOVER_CAPABLE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

class ColorEchoController extends HTMLElement {
  /** @type {Map<string, {accent: string, tint: string, onAccent: string, hover: string, accentBorder: string, accentInk: string, isBase: boolean}>} */
  #map = new Map();

  /** @type {Element | Document | null} */
  #scope = null;

  /** @type {string} */
  #hoverStrength = 'reduced';

  connectedCallback() {
    this.#parseMap();
    // Zero work when the product has no linked colors (ruling D8).
    if (this.#map.size === 0) return;

    this.#scope = this.closest('section, .shopify-section, .shopify-block') ?? document;
    document.documentElement.classList.add('ce-ready');
    this.#mirrorConfig();
    this.#tagTintSurface();

    this.#scope.addEventListener('change', this.#handleChange);
    // R1: "off" disables the hover preview wiring entirely (desktop-only
    // gating via HOVER_CAPABLE is unchanged).
    this.#hoverStrength = this.dataset.hoverStrength || 'reduced';
    if (HOVER_CAPABLE && this.#hoverStrength !== 'off') {
      this.#scope.addEventListener('pointerover', this.#handlePointerOver);
      this.#scope.addEventListener('pointerout', this.#handlePointerOut);
    }

    // Section Rendering API re-renders reconnect us; re-resolve rather than
    // clear-then-apply so an already-active echo never flashes.
    this.#syncFromSelection();
  }

  disconnectedCallback() {
    if (!this.#scope) return;
    this.#scope.removeEventListener('change', this.#handleChange);
    this.#scope.removeEventListener('pointerover', this.#handlePointerOver);
    this.#scope.removeEventListener('pointerout', this.#handlePointerOut);
    this.#scope = null;
    // Morph/Section Rendering re-renders disconnect and reconnect us within
    // the same task; only a TRUE removal (still detached at microtask time,
    // e.g. the block deleted in the editor) reverts the page.
    queueMicrotask(() => {
      if (this.isConnected) return;
      const root = document.documentElement;
      this.#clearTokens();
      this.#clearHoverPreview();
      root.classList.remove('ce-ready');
      for (const key of CE_MIRRORED_DATA) {
        delete root.dataset[key];
      }
      for (const surface of document.querySelectorAll('.ce-tint-surface')) {
        surface.classList.remove('ce-tint-surface');
      }
    });
  }

  #parseMap() {
    const tag = this.querySelector('script[data-color-echo-map]');
    if (!tag) return;
    try {
      const parsed = JSON.parse(tag.textContent ?? '{}');
      for (const [id, tokens] of Object.entries(parsed)) {
        this.#map.set(id, tokens);
      }
    } catch {
      // Malformed map -> the feature stays inert (ruling D8).
    }
  }

  /**
   * Mirrors the render-time block settings from this element's data-* config
   * onto <html>, once at connect (Phase 3 mirror pattern): the data-ce-*
   * flags become html[data-ce-*] hooks for the static stylesheet, and the
   * transition durations / hover dilution become custom properties. A 0ms
   * duration effectively disables that transition.
   */
  #mirrorConfig() {
    const root = document.documentElement;
    for (const key of CE_MIRRORED_DATA) {
      const value = this.dataset[key];
      if (value != null && value !== '') root.dataset[key] = value;
    }
    const surfaceMs = Number.parseInt(this.dataset.transitionSurfaceMs ?? '', 10);
    if (Number.isFinite(surfaceMs)) {
      root.style.setProperty('--ce-transition-surface', `${surfaceMs}ms`);
    }
    const buttonMs = Number.parseInt(this.dataset.transitionButtonMs ?? '', 10);
    if (Number.isFinite(buttonMs)) {
      root.style.setProperty('--ce-transition-button', `${buttonMs}ms`);
    }
    // Pre-formatted percentage string (e.g. "50%") straight from Liquid.
    if (this.dataset.hoverDilution) {
      root.style.setProperty('--ce-hover-dilution', this.dataset.hoverDilution);
    }
  }

  /**
   * Tags the owning product-information section wrapper as the buy-box tint
   * surface. Protected zones (ruling D4) are respected twice: skipped here,
   * and excluded again in CSS in case the attribute arrives after tagging.
   */
  #tagTintSurface() {
    const surface = this.closest('.shopify-section');
    if (surface && !surface.closest('[data-color-echo="off"]')) {
      surface.classList.add('ce-tint-surface');
    }
  }

  /**
   * Locates the sibling <variant-picker>, re-queried on EVERY call — the
   * picker morph re-renders itself on each option change, so a cached
   * reference would go stale (same lookup as pdp-color-gallery.js).
   *
   * @returns {Element | null} The sibling variant picker, if any.
   */
  #getVariantPicker() {
    return (
      this.parentElement?.querySelector('variant-picker') ??
      this.closest('section, .shopify-section, .shopify-block')?.querySelector('variant-picker') ??
      null
    );
  }

  /**
   * Delegated change handler. Re-resolves from the current selection rather
   * than trusting event.target: a non-color option change must not disturb
   * the echo, and the picker replaces its inputs after every change. Accepts
   * both picker styles: swatch/pill radios and the "dropdowns" variant_style
   * selects (whose options carry the same data-option-value-id).
   *
   * @param {Event} event - The bubbling change event.
   */
  #handleChange = (event) => {
    const input = event.target;
    const isRadio =
      input instanceof HTMLInputElement &&
      input.matches('variant-picker input[type="radio"][data-option-value-id]');
    const isDropdown =
      input instanceof HTMLSelectElement &&
      input.matches('variant-picker select') &&
      input.selectedOptions[0]?.hasAttribute('data-option-value-id');
    if (!isRadio && !isDropdown) return;
    // A committed selection outranks any in-flight hover preview: drop the
    // diluted state before syncing so the picked color renders full strength
    // immediately instead of waiting for pointerout.
    this.#clearHoverPreview();
    this.#syncFromSelection();
  };

  #syncFromSelection() {
    const tokens = this.#selectedTokens();
    if (tokens && !tokens.isBase) {
      this.#applyTokens(tokens);
    } else {
      // Base (black-family) selections render identical to the no-echo default.
      this.#clearTokens();
    }
  }

  /**
   * @returns {object | null} Tokens for the selected color value, if mapped.
   *   Checked radios and selected dropdown options are interchangeable: both
   *   carry data-option-value-id, and only color values are in the map.
   */
  #selectedTokens() {
    const selected = this.#getVariantPicker()?.querySelectorAll(
      'input[type="radio"][data-option-value-id]:checked, select option:checked[data-option-value-id]'
    );
    for (const element of selected ?? []) {
      const entry = element instanceof HTMLElement && this.#map.get(element.dataset.optionValueId ?? '');
      if (entry) return entry;
    }
    return null;
  }

  /**
   * @param {{accent: string, tint: string, onAccent: string, hover: string, accentBorder: string, accentInk?: string, buttonAccent?: string, ringWidth?: string}} tokens - Token values.
   */
  #applyTokens(tokens) {
    const root = document.documentElement;
    // Defensive hover-state drop (the primary one lives in #handleChange): a
    // morph mid-hover could re-apply tokens with the preview class stranded
    // on <html>, freezing the diluted derivation.
    this.#clearHoverPreview();
    // Inline custom properties on <html> carry the RAW token values; the
    // html.ce-active derivation rule in color-echo-styles.liquid maps them to
    // the consumed --ce-* tokens (diluted under a reduced hover preview).
    root.style.setProperty('--ce-raw-accent', tokens.accent);
    root.style.setProperty('--ce-raw-tint', tokens.tint);
    root.style.setProperty('--ce-raw-on-accent', tokens.onAccent);
    root.style.setProperty('--ce-raw-hover', tokens.hover);
    // Border token: equals accent except for very light accents (treatment-
    // resolved in Liquid) so white buttons keep an outline and tinted
    // icons/stars stay visible (Phase 2 item 5). ?? fallbacks cover a
    // stale cached map that predates each token.
    root.style.setProperty('--ce-raw-accent-border', tokens.accentBorder ?? tokens.accent);
    // Ink token: accent darkened in Liquid until it clears WCAG 4.5 on
    // white — for accent-colored text/glyphs only.
    root.style.setProperty('--ce-raw-accent-ink', tokens.accentInk ?? tokens.accentBorder ?? tokens.accent);
    // Buttons may diverge from the true accent (R4 contrast fallback / R3 ink
    // light treatment); surfaces and icons keep the accent.
    root.style.setProperty('--ce-raw-button-accent', tokens.buttonAccent ?? tokens.accent);
    root.style.setProperty('--ce-raw-ring-width', tokens.ringWidth ?? '1px');
    root.classList.add('ce-active');
  }

  #clearTokens() {
    const root = document.documentElement;
    root.classList.remove('ce-active');
    for (const property of CE_PROPS) {
      root.style.removeProperty(property);
    }
  }

  /** Drops the hover-preview class + strength attr (idempotent). */
  #clearHoverPreview() {
    document.documentElement.classList.remove('ce-hover-preview');
    delete document.documentElement.dataset.ceHoverStrength;
  }

  /**
   * Desktop-only hover preview reverting on pointerout. `data-hover-strength`
   * is the block setting (R1): "reduced" (default) shows the hovered color
   * diluted toward white — the raw tokens are applied at full strength and
   * the CSS derivation rule keyed on data-ce-hover-strength='reduced' does
   * the dilution; "full" previews at full strength; "off" never wires these
   * handlers (see connectedCallback).
   *
   * @param {PointerEvent} event - The delegated pointerover event.
   */
  #handlePointerOver = (event) => {
    const label = this.#hoverLabel(event.target);
    if (!label) return;
    // Ignore moves between children of the same label.
    if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

    const tokens = this.#labelTokens(label);
    if (tokens === undefined) return;

    // Tokens first: #applyTokens defensively drops any hover state, so the
    // preview class must be (re-)added after it.
    if (tokens && !tokens.isBase) {
      this.#applyTokens(tokens);
    } else {
      this.#clearTokens();
    }
    document.documentElement.classList.add('ce-hover-preview');
    document.documentElement.dataset.ceHoverStrength = this.#hoverStrength;
  };

  /**
   * @param {PointerEvent} event - The delegated pointerout event.
   */
  #handlePointerOut = (event) => {
    const label = this.#hoverLabel(event.target);
    if (!label) return;
    if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

    this.#clearHoverPreview();
    this.#syncFromSelection();
  };

  /**
   * @param {EventTarget | null} target - The event target.
   * @returns {Element | null} The enclosing swatch label, if any.
   */
  #hoverLabel(target) {
    return target instanceof Element
      ? target.closest('label.variant-option__button-label--has-swatch')
      : null;
  }

  /**
   * @param {Element} label - A swatch label.
   * @returns {object | null | undefined} Tokens for the label's radio;
   *   undefined when the value is not in the map (echo untouched).
   */
  #labelTokens(label) {
    const input = label.querySelector('input[data-option-value-id]');
    if (!(input instanceof HTMLElement) || !input.dataset.optionValueId) return undefined;
    return this.#map.get(input.dataset.optionValueId) ?? undefined;
  }
}

if (!customElements.get('color-echo-controller')) {
  customElements.define('color-echo-controller', ColorEchoController);
}
